import {computed, reactive, ref, watch, onMounted} from "vue";
import trans, { locale } from "helpers/_translation";
import { getQuery, hasQuery, getUrlWithQueryItemReplaced } from "helpers/_structure";
import {
	inboxStore,
	resetInbox,
	getInbox,
	patchInbox,
	favoriteInbox,
	readInbox,
	updateCount,
	getInboxAttachments
} from "store/inbox-store";

export const isSent = (item) => {
	return computed(() => {
		return !("sender_name" in item.data);
	}).value;
};
export const source = (item) => {
	return computed(() => {
		return item?.data?.source ?? "email";
	}).value;
};
export const read = (item) => {
	return computed(() => {
		if (isSent(item)) {
			// Means it is a sent item, return true by default
			return true;
		}
		return item._meta.read;
	}).value;
};
export const favorite = (item) => {
	return computed(() => {
		return item._meta.favorite;
	}).value;
};
export const important = (item) => {
	return computed(() => {
		return item.data.important === "Y";
	}).value;
};
export const date = (item) => {
	return computed(() => {
		return item.data?.receive_date ?? item.data.version_date;
	}).value;
};
export const contact_name = (item) => {
	return computed(() => {
		return item.data.sender_name ?? "";
	}).value;
};
export const contact_uid = (item) => {
	return computed(() => {
		return item.data.sender_uid ?? item.data.to ?? "";
	}).value;
};
export const attachments = (item) => {
	return new Promise((resolve, reject) => {
		if (!item?.data?.id) {
			return resolve([]);
		}

		getInboxAttachments(item.data.id).then((result) => {
			resolve(result);
		}).catch((error) => {
			reject(error);
		});
	});
};
export const attachmentCount = (item) => {
	return computed(() => {
		return parseInt(item._meta.attachments ?? 0);
	}).value;
};


// Functions
export const routeData = reactive({
	limit: 100,
	coupled: hasQuery("coupled") ? true : false, //
	page: getQuery("page") ?? 1,
	search: getQuery("search") ?? "",
	action: "overview",
	module_object: null,
	module_item_id: null
});
watch(
	() => inboxStore.current,
	() => {
		if (!inboxStore.current?.data) return;
		if (source(inboxStore.current) === "email") {
			const uid = contact_uid(inboxStore.current);
			if (!(uid in sha256cache)) {
				sha256cache[uid] = null;
				sha256(uid).then((hash) => {
					sha256cache[uid] = hash;
				});
			}
		}
	},
	{
		immediate: true
	}
);


export const handleFavoriteToggle = (item) => {
	const previousValue = item._meta.favorite;
	favoriteInbox(item.data.id).then(() => {
		item._meta.favorite = !previousValue;
		if (routeData.action === "favorite") {
			// Remove item from list
			inboxStore.items = inboxStore.items.filter((i) => i.data.id !== item.data.id);
		}
		updateCount({action: "favorite"});
	});
};
export const handleImportantToggle = (item) => {
	const previousValue = item.data.important === "Y";
	patchInbox(item.data.id, { important: previousValue ? "N" : "Y" }).then(() => {
		item.data.important = previousValue ? "N" : "Y";
		if (routeData.action === "important") {
			// Remove item from list
			inboxStore.items = inboxStore.items.filter((i) => i.data.id !== item.data.id);
		}
		updateCount({action: "important"});
	});
};
export const handleRead = (item) => {
	return new Promise((resolve, reject) => {
		const previousValue = item._meta.read;
		if (previousValue) {
			return resolve();
		}
		readInbox(item.data.id).then(() => {
			const realtimeItem = inboxStore.items.find(i => `${i.data.id}` === `${item.data.id}`);
			if (realtimeItem) {
				realtimeItem._meta.read = !previousValue;
			}
			resolve();
		});
	});
};
export const handleUnread = (item) => {
	return new Promise((resolve, reject) => {
		const previousValue = item._meta.read;
		if (!previousValue) {
			return resolve();
		}
		readInbox(item.data.id).then(() => {
			const realtimeItem = inboxStore.items.find(i => `${i.data.id}` === `${item.data.id}`);
			if (realtimeItem) {
				realtimeItem._meta.read = !previousValue;
			}
			resolve();
		});
	});
};



export const formatDate = (date, tmpLocale = locale) => {
	tmpLocale = tmpLocale || "nl-NL";

	return computed(() => {
		const options = {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
			locale: tmpLocale // set the locale to Dutch (Netherlands)
		};

		// Make sure date is a Date object
		if (!(date instanceof Date)) {
			date = new Date(date);
		}

		// Check if date is valid
		if (isNaN(date.getTime())) {
			return "";
		}
		return date.toLocaleDateString(tmpLocale, options);
	}).value;
};

export const formatTime = (date, locale) => {
	locale = locale || "nl-NL";

	return computed(() => {
		const options = {
			hour: 'numeric',
			minute: 'numeric'
		};
		// Make sure date is a Date object
		if (!(date instanceof Date)) {
			date = new Date(date);
		}

		// Check if date is valid
		if (isNaN(date.getTime())) {
			return "";
		}
		return date.toLocaleTimeString(locale, options);
	}).value;
};

export const formatDateTime = (date, locale) => {
	locale = locale || "nl-NL";

	return computed(() => {
		const dateOptions = {
			day: 'numeric',
			month: 'long',
			year: 'numeric',
			locale: locale // set the locale to Dutch (Netherlands)
		};
		const timeOptions = {
			hour: 'numeric',
			minute: 'numeric'
		};
		// Make sure date is a Date object
		if (!(date instanceof Date)) {
			date = new Date(date);
		}

		// Check if date is valid
		if (isNaN(date.getTime())) {
			return "";
		}
		return date.toLocaleDateString(locale, dateOptions) + ' ' + date.toLocaleTimeString(locale, timeOptions);
	}).value;
};

export const twoWeeks = 14 * 24 * 60 * 60 * 1000; /* ms */

export const selected = (item) => {
	return computed(() => {
		return inboxStore.selected.includes(`${item.data.id}`);
	}).value;
};
export const selectedAll = computed(() => {
	if (!inboxStore.items.length || inboxStore.loading) {
		return false;
	}
	return inboxStore.selected.length === inboxStore.items.length;
});
export const handleSelect = (item) => {
	if (selected(item)) {
		inboxStore.selected = inboxStore.selected.filter((id) => id !== `${item.data.id}`);
	} else {
		inboxStore.selected.push(`${item.data.id}`);
	}
};
export const handleSelectAll = () => {
	if (inboxStore.selected.length === inboxStore.items.length) {
		inboxStore.selected = [];
	} else {
		inboxStore.selected = inboxStore.items.map((item) => `${item.data.id}`);
	}
};


export const sha256 = async (message) => {
	// Encode the message as a Uint8Array (utf-8 by default)
	const msgBuffer = new TextEncoder().encode(message);

	// Hash the message
	const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

	// Convert the ArrayBuffer to an Array of bytes
	const hashArray = Array.from(new Uint8Array(hashBuffer));

	// Convert bytes to a hexadecimal string
	const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

	return hashHex;
};

export const sha256cache = reactive({});

export const avatar = (item) => {
	return computed(() => {
		if ((contact_uid(item) in sha256cache) && sha256cache[contact_uid(item)]) {
			return `//www.gravatar.com/avatar/${sha256cache[contact_uid(item)]}`;
		}
		return "/public/themes/www/_compiled/images/avatar.png";
	}).value;
};
export const html = (item) => {
	return computed(() => {
		if (item.data.html === null && item.data.text) {
			return `<style>body{margin:0;}</style><body>${item.data.text.replace(/([^>\r\n]?)(\r\n|\n\r)/g, '$1<br />$2')}</body>`;
		}
		return `<style>body{margin:0;}</style>${item.data.html}`;
	}).value;
};

export const iframe = ref();
export const adjustIframeHeight = () => {
	if (!iframe.value) return;
	iframe.value.style.height = `${iframe.value.contentWindow.document.body.scrollHeight}px`;
};

// Mounted / Unmounted handling
let inboxWatchers = [];
const handlePopstate = (event) => {
	if (event.state && event.state.path) {
		const url = new URL(event.state.path);
		const search = url.searchParams.get("search") ?? "";
		const page = url.searchParams.get("page") ?? 1;
		Object.assign(routeData, { search, page });
	}
};
export const handleMounted = () => {
	["page", "search", "coupled"].forEach(key => {
		inboxWatchers.push(watch(
			() => routeData[key],
			(newValue, oldValue) => {
				if ((key === "page") && !oldValue) return;
				if (!oldValue && !newValue) return;
				const newUrl = getUrlWithQueryItemReplaced(key, newValue);
				window.history.pushState({ path: newUrl }, '', newUrl);
			},
			{
				immediate: true
			}
		));
	});
	// Listen to history push state change
	window.addEventListener('popstate', handlePopstate);
};
export const handleUnmounted = () => {
	inboxWatchers.forEach((unwatch) => unwatch());
	window.removeEventListener('popstate', handlePopstate);
};